import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { type CampaignStatus, CampaignStatusLabels } from '@monitoring-panel/types';
import { api } from '@/services/api';

export function useCampaigns(params?: { status?: number[]; search?: string }) {
  return useQuery({
    queryKey: ['campaigns', params],
    queryFn: () => api.campaigns.list(params),
    staleTime: 30000, // 30 seconds
  });
}

export function useCampaign(executionId: string) {
  return useQuery({
    queryKey: ['campaign', executionId],
    queryFn: () => api.campaigns.get(executionId),
    enabled: !!executionId,
  });
}

export function useCampaignSummary(executionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['campaign-summary', executionId],
    queryFn: () => api.campaigns.getSummary(executionId),
    enabled: enabled && !!executionId,
    staleTime: 30000,
  });
}

export function useCampaignMetrics(executionId: string) {
  return useQuery({
    queryKey: ['campaign-metrics', executionId],
    queryFn: async () => {
      const result = await api.campaigns.getMetrics(executionId);
      return result;
    },
    enabled: !!executionId,
  });
}

export function useUpdateExecutionStatus(executionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: CampaignStatus) =>
      api.executions.updateStatus(executionId, { status }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-summary', executionId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success(`Status updated to ${CampaignStatusLabels[data.status]}`);
    },
    onError: (error) => {
      toast.error(`Failed to update status: ${error.message}`);
    },
  });
}

export function useRefreshCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionId?: string) =>
      executionId ? api.cache.refresh(executionId) : api.cache.refreshAll(),
    onSuccess: (data, executionId) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-summary'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics'] });

      if (executionId) {
        toast.success(`Refreshed ${executionId}`);
      } else {
        toast.success(`Refreshed ${data.refreshed.length} campaigns`);
      }

      if (data.failed.length > 0) {
        toast.error(`${data.failed.length} campaigns failed to refresh`);
      }
    },
    onError: (error) => {
      toast.error(`Refresh failed: ${error.message}`);
    },
  });
}
